import React, { useEffect, useRef } from 'react';
import { Button, Input, Radio } from 'antd';
import FileUpload from 'components/form/FileUploadPrivate';
import type { AdditionalW9Dto, AdditionalW9Item } from '../types';
import { additionalW9Complete, additionalW9ItemValid, TIN_RE } from '../validators';
import s from '../common.module.less';

interface Props {
    // action needed 深链定位：高亮各 taxId 对应的条目；未录入过的会自动追加预填
    // taxId + 表上名字（新协议 optumW9Required 的 entityName）的条目卡供补录
    focusEntries?: { taxId: string; nameOnW9?: string }[];
    value: AdditionalW9Dto;
    onChange: (dto: AdditionalW9Dto) => void;
    onContinue: () => void;
    continueLabel?: string;
    // group 宿主（ProviderPayerModal）传 true：零侧边对齐标题、footer 分隔线通边
    flush?: boolean;
    // review 行内编辑：只渲染表单 body，动作按钮由宿主（ReviewSection 吸底条）提供
    hideFooter?: boolean;
    onBack?: () => void;
    saving?: boolean;
}

const normTin = (v?: string) => (v || '').replace(/-/g, '').trim();

// UnitedHealthcare 的 W-9（09/01 重构）：一条记录 = Tax ID + 表上名字（legal name 或 DBA），与州无关。
// primary（Verification 种子）完整时渲染只读 summary（Change 仅换文件，TIN/名字回源头改）；
// 不完整（group 未填 Verification / legacy）降级为可编辑卡。Yes/No 是数据驱动的：有额外条目即 Yes，
// 删光自动回 No。文案不提 Optum——provider 只选过 UnitedHealthcare（原型同款处理）。
const OptumW9Step = ({
    value,
    onChange,
    onContinue,
    onBack,
    saving,
    continueLabel = 'Continue',
    flush,
    hideFooter,
    focusEntries,
}: Props) => {
    // 渲染源：保证始终有一张 primary 卡。虚拟空 primary 不落库（不 emit），用户实际编辑时
    // 才随 emit 物化——宿主的 Verification 种子（mergeSeededW9）能顶替空 primary 补预填
    const savedItems = value.items || [];
    const items: AdditionalW9Item[] = savedItems.some((it) => it.primary)
        ? savedItems
        : [{ primary: true }, ...savedItems];
    const extras = items.filter((it) => !it.primary);

    const emit = (nextItems: AdditionalW9Item[]) =>
        onChange({ hasOtherTaxIds: nextItems.some((it) => !it.primary), items: nextItems });

    // 深链条目从未录入过：自动追加预填 taxId + 表上名字的 other 条目卡（对齐旧协议里
    // focus state 不在已存数据时合成占位的处理；深链不注入 primary），随链内/行内保存落库；
    // 已存在同 TIN 的只高亮不重复注入
    const focusInjectedRef = useRef(false);
    useEffect(() => {
        if (!focusEntries?.length || focusInjectedRef.current) return;
        focusInjectedRef.current = true;
        const missing = focusEntries.filter((e) => !savedItems.some((it) => normTin(it.taxId) === normTin(e.taxId)));
        if (missing.length) {
            emit([
                ...savedItems,
                ...missing.map((e) => ({ taxId: e.taxId, nameOnW9: e.nameOnW9, nameType: 'DBA' as const })),
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusEntries]);

    const focusTins = (focusEntries || []).map((e) => normTin(e.taxId));
    const isFocused = (it: AdditionalW9Item) => !!normTin(it.taxId) && focusTins.includes(normTin(it.taxId));
    const firstFocusIdx = items.findIndex(isFocused);
    const focusRef = useRef<HTMLDivElement | null>(null);
    const focusScrolledRef = useRef(false);
    useEffect(() => {
        if (firstFocusIdx >= 0 && focusRef.current && !focusScrolledRef.current) {
            focusScrolledRef.current = true;
            focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [firstFocusIdx]);

    // primary 只有「到手就是填好的」（Verification 种子 / 已存数据）才收成只读 summary；
    // 本次会话手动编辑过 TIN/名字/类型的按普通条目卡显示。Change 换文件不算编辑（summary 态本就允许换文件）
    const primaryTouchedRef = useRef(false);

    const patchItem = (idx: number, patch: Partial<AdditionalW9Item>) => {
        const fileOnly = Object.keys(patch).length === 1 && 'fileUrl' in patch;
        if (items[idx]?.primary && !fileOnly) primaryTouchedRef.current = true;
        const next = [...items];
        next[idx] = { ...next[idx], ...patch };
        emit(next);
    };

    const removeItem = (idx: number) => emit(items.filter((_, i) => i !== idx));

    const setHasExtras = (yes: boolean) => {
        if (yes) {
            if (!extras.length) emit([...items, { nameType: 'DBA' }]);
        } else {
            // 回 No 丢弃额外条目；primary 始终保留（原型同款）
            emit(items.filter((it) => it.primary));
        }
    };

    const tinError = (it: AdditionalW9Item) => {
        const v = (it.taxId || '').trim();
        return v && !TIN_RE.test(v) ? 'Enter a valid Tax ID / EIN (XX-XXXXXXX).' : '';
    };

    const entryCard = (it: AdditionalW9Item, idx: number, ordinal: number, removable: boolean) => (
        <div
            key={idx}
            ref={idx === firstFocusIdx ? focusRef : undefined}
            className={`${s.govCard} ${isFocused(it) ? s.w9Focus : ''}`}
        >
            <div className={s.w9EntryHeader}>
                <span className={s.govCardTitle} style={{ marginBottom: 0 }}>
                    W-9 form {ordinal}
                </span>
                {removable && (
                    <Button size="small" onClick={() => removeItem(idx)}>
                        Remove
                    </Button>
                )}
            </div>
            <div className={s.w9Row}>
                <div>
                    <span className={s.fieldLabel}>Tax ID / EIN</span>
                    <Input
                        value={it.taxId}
                        placeholder="XX-XXXXXXX"
                        onChange={(e) => patchItem(idx, { taxId: e.target.value })}
                    />
                    {tinError(it) && <div className={s.fieldError}>{tinError(it)}</div>}
                </div>
                <div>
                    <span className={s.fieldLabel}>Name on the W-9</span>
                    <Input
                        value={it.nameOnW9}
                        placeholder="Exactly as printed on the form"
                        onChange={(e) => patchItem(idx, { nameOnW9: e.target.value })}
                    />
                </div>
            </div>
            <Radio.Group
                className={s.w9NameType}
                value={it.nameType === 'DBA' ? 'DBA' : 'LEGAL_NAME'}
                onChange={(e) => patchItem(idx, { nameType: e.target.value })}
            >
                <Radio value="LEGAL_NAME">Legal name</Radio>
                <Radio value="DBA">DBA / business name</Radio>
            </Radio.Group>
            <FileUpload
                uploadType="LICENSE"
                accept=".pdf,.jpg,.jpeg,.png"
                tips="PNG, JPG, PDF up to 10MB"
                value={it.fileUrl || ''}
                onChange={(fileUrl) => patchItem(idx, { fileUrl })}
            />
        </div>
    );

    // Verification 带来的 W-9 已完整：只读 summary，Change 仅换文件（TIN/名字如有误回 Verification 改）
    const summaryCard = (it: AdditionalW9Item, idx: number) => (
        <div key={idx} ref={idx === firstFocusIdx ? focusRef : undefined} className={isFocused(it) ? s.w9Focus : ''}>
            <div className={s.docTitle}>W-9 form</div>
            <FileUpload
                uploadType="LICENSE"
                accept=".pdf,.jpg,.jpeg,.png"
                tips="PNG, JPG, PDF up to 10MB"
                value={it.fileUrl || ''}
                onChange={(fileUrl) => patchItem(idx, { fileUrl })}
            />
        </div>
    );

    const complete = additionalW9Complete({ ...value, items });

    // primary 在前渲染；额外条目的序号从 2 开始（与原型 "W-9 form N" 一致）
    const primaryIdx = items.findIndex((it) => it.primary);
    const orderedIdx = [
        ...(primaryIdx >= 0 ? [primaryIdx] : []),
        ...items.map((_, i) => i).filter((i) => i !== primaryIdx),
    ];

    return (
        <>
            <div className={`${s.modalBody} ${flush ? s.flushBody : ''}`}>
                {orderedIdx
                    .filter((i) => items[i].primary)
                    .map((i) =>
                        additionalW9ItemValid(items[i]) && !primaryTouchedRef.current
                            ? summaryCard(items[i], i)
                            : entryCard(items[i], i, 1, false),
                    )}
                <div className={s.w9Question}>
                    Have you billed claims under any other Tax ID or business name (DBA)?
                </div>
                <Radio.Group
                    value={extras.length ? 'yes' : 'no'}
                    onChange={(e) => setHasExtras(e.target.value === 'yes')}
                >
                    <Radio value="yes">Yes</Radio>
                    <Radio value="no">No</Radio>
                </Radio.Group>
                {extras.length > 0 && (
                    <div className={s.w9Extras}>
                        {orderedIdx.filter((i) => !items[i].primary).map((i, j) => entryCard(items[i], i, j + 2, true))}
                        <Button
                            type="link"
                            className={s.addW9Link}
                            onClick={() => emit([...items, { nameType: 'DBA' }])}
                        >
                            + Add another W-9
                        </Button>
                    </div>
                )}
            </div>
            {!hideFooter && (
                <div className={`${s.modalFooter} ${flush ? s.flushFooter : ''}`}>
                    <Button
                        type="primary"
                        block
                        size="large"
                        className={s.primaryBtn}
                        disabled={!complete}
                        loading={saving}
                        onClick={onContinue}
                    >
                        {continueLabel}
                    </Button>
                    {onBack && (
                        <Button block size="large" className={s.backBtn} onClick={onBack}>
                            Back
                        </Button>
                    )}
                </div>
            )}
        </>
    );
};

export default OptumW9Step;
